import { normalizeError } from '../api/core/errors.ts';
import { msg, type MaybeMsg, type Msg } from '../i18n/msg.ts';

/**
 * The bank's own wording for the failures the generic mapper cannot name.
 *
 * Kept pure and apart from the store so the suite can reach it: the store itself is all requests.
 * Every message is a Msg (key + params): the screen translates it at render, so a stored one follows a language switch.
 */

/** No IZTeach teacher session at all - the bank cannot be reached until /setup is done. */
export function setupSignedOutMessage(): Msg {
  return msg('questionBank:messages.setupSignedOut');
}

/** A session exists but it is the student token from the QLT exchange, which the bank refuses. */
export function setupNotTeacherMessage(): Msg {
  return msg('questionBank:messages.setupNotTeacher');
}

/** The gateway authorizer refused the token's role; the empty 403 body says nothing useful. */
export function setupForbiddenMessage(): Msg {
  return msg('questionBank:messages.setupForbidden');
}

/** The IZ account could not be read, so "Của tôi" cannot filter. */
export function setupMineUnknownMessage(): Msg {
  return msg('questionBank:messages.setupMineUnknown');
}

/** How the teacher gets an IZ teacher session back: /setup, or with SSO a fresh QLT login (there is no /setup). */
export type SessionRemedy = 'setup' | 'sso';

export interface SessionMessages {
  signedOut: Msg;
  notTeacher: Msg;
  forbidden: Msg;
  mineUnknown: Msg;
}

export function sessionMessages(remedy: SessionRemedy = 'setup'): SessionMessages {
  if (remedy === 'setup') {
    return {
      signedOut: setupSignedOutMessage(),
      notTeacher: setupNotTeacherMessage(),
      forbidden: setupForbiddenMessage(),
      mineUnknown: setupMineUnknownMessage(),
    };
  }
  return {
    signedOut: msg('questionBank:messages.ssoSignedOut'),
    notTeacher: msg('questionBank:messages.ssoNotTeacher'),
    forbidden: msg('questionBank:messages.ssoForbidden'),
    mineUnknown: msg('questionBank:messages.ssoMineUnknown'),
  };
}

/** A 403 here is the role check, not a missing question. Null for every other failure. */
export function forbiddenMessage(error: unknown, remedy: SessionRemedy = 'setup'): Msg | null {
  return normalizeError(error).kind === 'forbidden' ? sessionMessages(remedy).forbidden : null;
}

/**
 * Sequential saves stop at the first failure, so the teacher is told exactly where: what landed,
 * which one failed, and how many were never sent.
 */
export function partialSaveMessage(saved: number, total: number, reason: MaybeMsg): Msg {
  const failedAt = saved + 1;
  const unsent = total - failedAt;
  const params = { saved, total, failedAt, reason, count: unsent };
  if (saved > 0 && unsent > 0) return msg('questionBank:messages.partialSavedFailedUnsent', params);
  if (saved > 0) return msg('questionBank:messages.partialSavedFailed', params);
  if (unsent > 0) return msg('questionBank:messages.partialFailedUnsent', params);
  return msg('questionBank:messages.partialFailed', params);
}

/** The note when a page holds question types the board can only name; the count and pager include them. */
export function hiddenTypesMessage(hidden: number): Msg {
  return msg('questionBank:messages.hiddenTypes', { count: hidden });
}
